import {
  alpha,
  Box,
  Card,
  Divider,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { Circle } from "@mui/icons-material";

const StatCard = ({
  title,
  value,
  subtitle,
  color,
}: {
  title: string;
  value: string | number;
  subtitle?: string;
  color: string;
}) => (
  <Paper
    variant="outlined"
    sx={{ width: "100%", p: 2, border: "none", bgcolor: alpha(color, 0.05) }}
  >
    <Typography variant="caption" color={color} gutterBottom>
      {title}
    </Typography>
    <Typography variant="body2" fontWeight={600} color={color}>
      {value}
    </Typography>
    {subtitle && (
      <Typography variant="caption" color={color}>
        {subtitle}
      </Typography>
    )}
  </Paper>
);

const getRagColorFromValue = (
  value: number
): "success" | "warning" | "error" => {
  if (value >= 80) return "success";
  if (value >= 70) return "warning";
  return "error";
};

export const RegionCard = ({
  region,
  value,
  activities,
  color,
}: {
  region: string;
  value: number;
  activities: number;
  color: string;
}) => {
  const ragColor = getRagColorFromValue(value);

  return (
    <Card
      variant="outlined"
      sx={{
        p: 2,
        height: "100%",
        width: "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Circle color={ragColor} sx={{ fontSize: 10 }} />
          <Typography variant="subtitle2" fontWeight={600}>
            {region}
          </Typography>
        </Box>
        <Typography variant="h6" fontWeight={700} color={`${ragColor}.main`}>
          {value}%
        </Typography>
      </Box>

      <LinearProgress
        variant="determinate"
        value={value}
        color={ragColor}
        sx={{ height: 6, borderRadius: 1, mb: 1.5 }}
      />

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="caption" color="text.secondary">
          On-Time Delivery
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {activities} active activities
        </Typography>
      </Box>
    </Card>
  );
};

export default function MaterialsDashboard(data: { data: { "outstanding_requests": number, "avg_confirmation_time_days": number, "avg_confirmation_time_target_days": number, "delivery_reliability_percentage": number, "requests_causing_delays": number } }) {

  const theme = useTheme();

  return (
    <Grid container spacing={2} p={2}>
      <Grid size={3}>
        <StatCard title="Outstanding Requests" value={data.data.outstanding_requests} color={theme.palette.primary.main} />
      </Grid>
      <Grid size={3}>
        <StatCard title="Avg Confirmation Time" value={`${data.data.avg_confirmation_time_days} days (Target: ${data.data.avg_confirmation_time_target_days}d)`} color={theme.palette.warning.main} />
      </Grid>
      <Grid size={3}>
        <StatCard title="Delivery Reliability" value={`${data.data.delivery_reliability_percentage}%`} color={theme.palette.info.main} />
      </Grid>
      <Grid size={3}>
        <StatCard title="Requests Causing Delays" value={data.data.requests_causing_delays} color={theme.palette.error.main} />
      </Grid>
    </Grid>
  );

};